/**
 * Utilities for the datetime strings the Kodaris platform stores and returns from its APIs.
 */
export class KRDateUtils {
    /**
     * Parses a platform MySQL datetime string as UTC.
     *
     * The platform writes these as `yyyy-MM-dd HH:mm:ss:SSS` — space-separated, with the milliseconds
     * delimited by a COLON — and everything after the date is optional, so `"2026-01-28"`,
     * `"2026-01-28 01:02"` and `"2026-01-28 01:02:50:276"` are all valid. The colon before the
     * milliseconds isn't ISO-8601, so `new Date()` can't be trusted with these.
     *
     * Returns `null` rather than an Invalid Date when the value is empty or doesn't match, because
     * `Intl.DateTimeFormat#format` throws `RangeError` on an invalid date and would take down a whole
     * render instead of leaving one field blank.
     *
     * @param value A platform datetime string, e.g. `"2026-01-28 01:02:50:276"`.
     * @returns The instant as a `Date`, or `null` if the value can't be parsed.
     */
    static parseMysql(value) {
        if (!value) {
            return null;
        }
        // The platform delimits milliseconds with a colon, but accept a dot and any number of digits too
        // so a hand-written or third-party value still parses instead of being silently dropped.
        const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2})(?::(\d{2}))?(?::(\d{2}))?(?:[:.](\d{1,9}))?)?$/
            .exec(value.trim());
        if (!match) {
            return null;
        }
        // Digits after the seconds are a FRACTION of a second, so ":27" is 270ms, not 27ms — same as the
        // grid, which rewrites ":27" to ".27" and lets the engine read it. Pad to 3 and keep the millis.
        const ms = Number((`${match[7] || ''}000`).slice(0, 3));
        // Missing time components default to 0, matching the backend's parseDefaulting. Month is 1-based
        // in the string and 0-based in Date.UTC.
        return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4] || 0), Number(match[5] || 0), Number(match[6] || 0), ms));
    }
    /**
     * Turns any date value into a `Date` — a `Date` passes through, a platform MySQL string goes
     * through `parseMysql`, and anything else is handed to `new Date()` so ISO-8601, RFC 2822 and
     * epoch millisecond values all still work.
     *
     * Note the asymmetry inherited from `new Date()`: a MySQL string is read as UTC, while a bare
     * non-ISO string like `"2026-01-28 01:02:50.276"` (dot milliseconds) is read as LOCAL, because
     * that's how the engine parses it. Only the platform's own format is guaranteed UTC here.
     *
     * @param value A date in any of the forms above.
     * @returns The instant as a `Date`, or `null` if there's nothing valid to parse.
     */
    static parse(value) {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (value instanceof Date) {
            if (isNaN(value.getTime())) {
                return null;
            }
            return value;
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(value.trim())) {
            return KRDateUtils.parseMysql(value);
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return null;
        }
        return date;
    }
    /**
     * Formats any date value in the viewer's local timezone.
     *
     * Resolves the value with `parse`, so platform MySQL strings, ISO strings, epoch millis and `Date`
     * instances all work. Returns `''` when there's nothing valid to format —
     * `Intl.DateTimeFormat#format` throws `RangeError` on an invalid date, and one blank field beats a
     * render that dies.
     *
     * The locale is left as `undefined` so the browser's own locale decides, and no `timeZone` is set
     * so it converts the instant to the viewer's zone.
     *
     * @param value A date in any form `parse` accepts.
     * @param options Intl options, e.g. `{ year: 'numeric', month: 'short', day: 'numeric' }`.
     * @returns The formatted date, or `''` if there's nothing valid to format.
     */
    static format(value, options) {
        const date = KRDateUtils.parse(value);
        if (!date) {
            return '';
        }
        return new Intl.DateTimeFormat(undefined, options).format(date);
    }
}
//# sourceMappingURL=date-utils.js.map